import { useCallback, useMemo, useRef } from 'react'

import { f0FormField } from '@factorialco/f0-react'
import { TrainingRequestDetailFragment } from 'generated/resources/graphql'
import moment from 'moment'
import { z } from 'zod'

import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import { multiEmployeeSelectorV2FormField } from 'modules/employees/components/EmployeeSelectorV2/formField'

import { useUpdateTrainingRequest } from 'modules/trainings/hooks/TrainingRequests/useUpdateTrainingRequest'
import { useCompetencies } from 'modules/trainings/hooks/Trainings/useCompetencies'

const SCHEDULE_VALUES = ['workinghours', 'mixedhours', 'notsure', 'outsideworkinghours'] as const

function getScheduleOptions() {
  return [
    {
      label: i18n.t('trainings.training_requests.create.schedule_options.during_work_hours'),
      value: 'workinghours',
    },
    {
      label: i18n.t('trainings.training_requests.create.schedule_options.mixed_working_hours'),
      value: 'mixedhours',
    },
    {
      label: i18n.t('trainings.training_requests.create.schedule_options.not_sure'),
      value: 'notsure',
    },
    {
      label: i18n.t(
        'trainings.training_requests.create.schedule_options.outside_working_hours'
      ),
      value: 'outsideworkinghours',
    },
  ]
}

type UseNewProposalFormParams = {
  trainingRequest: TrainingRequestDetailFragment
  onSuccess?: () => void
}

export const useNewProposalForm = ({ trainingRequest, onSuccess }: UseNewProposalFormParams) => {
  const { update, loading: updating } = useUpdateTrainingRequest()
  const { competencies } = useCompetencies()
  const sendForApprovalRef = useRef(false)

  const setSendForApproval = useCallback((value: boolean) => {
    sendForApprovalRef.current = value
  }, [])

  const competencyOptions = useMemo(
    () =>
      (competencies ?? []).map((c) => ({
        label: c.name,
        value: c.id.toString(),
      })),
    [competencies]
  )

  const participantsField = useMemo(
    () =>
      multiEmployeeSelectorV2FormField({
        label: i18n.t('tasks.approvals.trainings_training_request.fields.participants'),
        section: 'participants',
        mandatory: false,
        fieldConfig: {
          options: { asList: true, size: 'md' },
        },
      }),
    []
  )

  const schema = z.object({
    // Basic information section
    trainingNeed: f0FormField(z.object({
      value: z.string().nullable().optional(),
      mentionIds: z.array(z.number()).optional(),
    }).optional(), {
      label: i18n.t('trainings.training_requests.my_requests.detail.training_need'),
      section: 'basic-info',
      fieldType: 'richtext',
      height: 'sm',
      plainHtmlMode: true,
    }),
    providerName: f0FormField(z.string().optional(), {
      label: i18n.t('trainings.training_requests.my_requests.detail.provider_name'),
      section: 'basic-info',
    }),
    externalReference: f0FormField(z.string().optional(), {
      label: i18n.t('trainings.training_requests.my_requests.detail.external_link'),
      section: 'basic-info',
    }),
    competencyIds: f0FormField(z.array(z.string()).optional(), {
      label: i18n.t('trainings.training_requests.create.competencies_label'),
      section: 'basic-info',
      multiple: true,
      showSearchBox: true,
      options: competencyOptions,
      placeholder: i18n.t('trainings.training_requests.create.select_placeholder'),
      renderIf: () => competencyOptions.length > 0,
    }),

    // Timings and costs section
    startDate: f0FormField(z.date().optional(), {
      label: i18n.t('trainings.training_requests.my_requests.detail.start_date'),
      section: 'timings',
      row: 'dates',
    }),
    endDate: f0FormField(z.date().optional(), {
      label: i18n.t('trainings.training_requests.my_requests.detail.end_date'),
      section: 'timings',
      row: 'dates',
    }),
    duration: f0FormField(z.number().optional(), {
      label: i18n.t('trainings.training_requests.my_requests.detail.total_training_hours'),
      section: 'timings',
      fieldType: 'number',
    }),
    schedule: f0FormField(z.enum(SCHEDULE_VALUES).optional(), {
      label: i18n.t('trainings.training_requests.my_requests.detail.training_schedule'),
      section: 'timings',
      options: getScheduleOptions(),
      placeholder: i18n.t('trainings.training_requests.create.select_placeholder'),
    }),
    cost: f0FormField(z.number().optional(), {
      label: i18n.t('trainings.training_requests.my_requests.detail.cost'),
      section: 'timings',
      fieldType: 'number',
    }),
    subsidized: f0FormField(z.boolean(), {
      label: i18n.t('trainings.training_requests.my_requests.detail.subsidized_cost'),
      helpText: i18n.t(
        'trainings.training_requests.my_requests.detail.subsidized_cost_description'
      ),
      section: 'timings',
      fieldType: 'switch',
    }),

    // Participants section
    participants: participantsField,

    // Additional information section
    comments: f0FormField(z.object({
      value: z.string().nullable().optional(),
      mentionIds: z.array(z.number()).optional(),
    }).optional(), {
      label: i18n.t('trainings.training_requests.my_requests.detail.comments'),
      section: 'additional-info',
      fieldType: 'richtext',
      height: 'sm',
      plainHtmlMode: true,
    }),
  })

  type FormValues = z.infer<typeof schema>

  const defaultValues: Partial<FormValues> = {
    trainingNeed: { value: trainingRequest.trainingNeed ?? '' },
    providerName: trainingRequest.providerName ?? '',
    externalReference: trainingRequest.externalReference ?? '',
    competencyIds: (trainingRequest.competencyIds ?? []).map(String),
    startDate: trainingRequest.startDate ? moment(trainingRequest.startDate).toDate() : undefined,
    endDate: trainingRequest.endDate ? moment(trainingRequest.endDate).toDate() : undefined,
    duration: trainingRequest.duration != null ? Number(trainingRequest.duration) : undefined,
    schedule: isValidSchedule(trainingRequest.schedule) ? trainingRequest.schedule : undefined,
    cost: trainingRequest.cost != null ? Number(trainingRequest.cost) : undefined,
    subsidized: trainingRequest.subsidized ?? false,
    participants: {
      type: 'manual',
      ids: (trainingRequest.employeeIds ?? []).map(String),
    },
    comments: { value: trainingRequest.comments ?? '' },
  }

  const sections = {
    'basic-info': {
      title: i18n.t('trainings.training_requests.my_requests.detail.basic_information'),
    },
    timings: {
      title: i18n.t('trainings.training_requests.my_requests.detail.timings_and_costs'),
    },
    participants: {
      title: i18n.t('tasks.approvals.trainings_training_request.fields.participants'),
    },
    'additional-info': {
      title: i18n.t(
        'trainings.training_requests.my_requests.detail.additional_information'
      ),
    },
  }

  const onSubmit = async (
    values: FormValues
  ) => {
    const sendForApproval = sendForApprovalRef.current
    const employeeIds =
      values.participants?.type === 'manual'
        ? values.participants.ids.map(String)
        : (trainingRequest.employeeIds ?? [])

    const result = await update({
      id: trainingRequest.id,
      trainingName: trainingRequest.trainingName,
      trainingNeed: values.trainingNeed?.value ?? undefined,
      comments: values.comments?.value ?? undefined,
      competencyIds: (values.competencyIds ?? []).map(String),
      employeeIds,
      status: sendForApproval ? 'review' : trainingRequest.status,
      subsidized: values.subsidized,
      cost: values.cost != null ? Number(values.cost) : undefined,
      duration: values.duration != null ? Number(values.duration) : undefined,
      startDate: values.startDate ? moment(values.startDate).format('YYYY-MM-DD') : undefined,
      endDate: values.endDate ? moment(values.endDate).format('YYYY-MM-DD') : undefined,
      schedule: values.schedule ?? undefined,
      externalReference: values.externalReference,
      providerName: values.providerName,
      trainingId: trainingRequest.training?.id,
    })

    const handler = handleResult(result, () => undefined)

    if (handler.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      onSuccess?.()
      return { success: true as const }
    }

    return { success: false as const }
  }

  return {
    schema,
    defaultValues,
    sections,
    onSubmit,
    updating,
    setSendForApproval,
  }
}

function isValidSchedule(
  value: string | null | undefined
): value is (typeof SCHEDULE_VALUES)[number] {
  if (value == null) return false
  const scheduleStrings: readonly string[] = SCHEDULE_VALUES
  return scheduleStrings.includes(value)
}
